import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import get from "lodash-es/get";
import { Manager, Reference, Popper } from "react-popper";
import IsolatedScroll from "react-isolated-scroll";
import AutoWhatever from "react-autowhatever";
import AutosizeInput from "react-input-autosize";

import {
  isFunction,
  gatherClasses,
  isEmpty,
  isObject,
  isDescendant,
  generateId
} from "../../lib/utils";
import InputFeedback from "../inputFeedback/inputFeedback";
import Indicators from "./Indicators";
import Tag from "./Tag";
import PartialTag from "./PartialTag";
import MenuItem from "./MenuItem";
import theme from "./theme";
import "./tagSelect.scss";

const debounce = require("debounce-promise");

class TagSelect extends Component {
  constructor(props) {
    super(props);

    const { debounceDelay } = this.props;
    const delayInt = parseInt(debounceDelay, 10);
    // eslint-disable-next-line no-restricted-globals
    const delay = !isNaN(delayInt) && delayInt >= 0 ? debounceDelay : 0;

    this.state = {
      popperKey: "INITIALIZE",
      highlightedItemIndex: null,
      partial: null,
      focused: false,
      inputValue: "",
      valueOptions: [],
      menuItems: []
    };

    this.inputRef = null; // Can't use createRef as autowhatever doesnt support

    this.controlRef = createRef();

    this.menuRef = createRef();

    this.autowhateverId = generateId("tag-select-dropdown-");

    this.debouncedLoadKeySuggestions = debounce(this.loadKeySuggestions, delay);
    this.debouncedLoadValueSuggestions = debounce(
      this.loadValueSuggestions,
      delay
    );
  }

  /**
   * It does 2 things on mount. First binds and event handler to
   * hide menu on blur of control and second if this tagSelect
   * comes under a modal, it toggles the popperKey to reset popper's
   * position.
   *
   * @returns {undefined}
   */
  componentDidMount() {
    const { autoFocus } = this.props;
    const modal = document.querySelector(".oui-modal");

    document.addEventListener("mousedown", this.handleDocumentMouseDown);

    if (modal !== null && isDescendant(modal, this.controlRef.current)) {
      setTimeout(() => {
        this.setState({
          popperKey: "RECREATE"
        });
      }, 1000);
    }

    if (autoFocus && this.inputRef) {
      this.inputRef.focus();
    }
  }

  /**
   * Removes document mousedown event handler created for menu hide.
   *
   * @return {undefined}
   */
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocumentMouseDown);
  }

  /**
   * Actual document mousedown handler.
   *
   * @param {SyntheticEvent} event generated event
   *
   * @return {undefined}
   */
  handleDocumentMouseDown = event => {
    const { target } = event;
    const outsideControl =
      this.controlRef.current && !this.controlRef.current.contains(target);
    const outsideMenu =
      this.menuRef.current && !this.menuRef.current.contains(target);

    if (outsideControl && outsideMenu) {
      this.triggerBlur(event);
    }
  };

  /**
   * Set formik value on change to support validations.
   *
   * @param {array} values array of values
   *
   * @return {undefined}
   */
  setFormikFieldValue = values => {
    const { validationProps, name } = this.props;

    if (validationProps && validationProps.setFieldValue && name) {
      validationProps.setFieldValue(name, values, true);
    }
  };

  /**
   * Set formik field touched on blur.
   *
   * @returns {undefined}
   */
  setFormikFieldTouched = () => {
    const { validationProps, name } = this.props;

    if (validationProps && validationProps.setFieldTouched && name) {
      validationProps.setFieldTouched(name);
    }
  };

  /**
   * It does a lot of things on blur of the control input.
   * Removes focused state, hide menu items, set formik touched,
   * blurs actual underlying input field and if user provided an
   * onBlur handler it calls that too.
   *
   * @param {SyntheticEvent} event generated event
   *
   * @return {undefined}
   */
  triggerBlur = event => {
    const { focused } = this.state;
    const { onBlur } = this.props;

    this.setState({ focused: false });

    this.hideSuggestions();

    if (this.inputRef) {
      this.inputRef.blur();
    }

    if (focused) {
      this.setFormikFieldTouched();
    }

    if (focused && isFunction(onBlur)) {
      onBlur(event);
    }
  };

  /**
   * Sets focus inside the input box whenever user clicks on
   * the control for e.g. on click of tag, close icon, dropdown
   * arrow or somewhere else.
   *
   * @return {undefined}
   */
  onControlContainerClick = () => {
    if (this.inputRef) {
      this.inputRef.focus();
    }
  };

  /**
   * When user click on clear icon of a single tag in
   * the control. This is a closure to hold the removing
   * item value.
   *
   * @param {number} index The index of removing item.
   *
   * @return {Function}
   */
  onRemoveTag = index => () => {
    const { onChange, onRemoveTag, value, name } = this.props;
    const removedItem = value[index];
    const cloned = [...value];

    cloned.splice(index, 1);

    if (isFunction(onChange)) {
      onChange(cloned, name);
    }

    if (isFunction(onRemoveTag)) {
      onRemoveTag(removedItem);
    }

    this.setFormikFieldValue(cloned);
  };

  /**
   * onClear is called when user click on clear icon in
   * the control. It calles user provided onChange as well
   * as sets formik field value.
   *
   * @return {undefined}
   */
  onClear = () => {
    const { onChange, name } = this.props;

    if (isFunction(onChange)) {
      onChange([], name);
    }

    this.setFormikFieldValue([]);
  };

  /**
   * This is called on focus of the input field which sets
   * the focused state of entire component to true to make it
   * blue shadowed as well as loadKeySuggestions or loadValueSuggestions.
   *
   * @param {SyntheticEvent} event generated event
   * @return {undefined}
   */
  onInputFocus = event => {
    const { inputValue, partial, focused } = this.state;
    const { onFocus } = this.props;

    this.setState({ focused: true });

    if (!focused && isFunction(onFocus)) {
      onFocus(event);
    }

    if (partial) {
      this.loadValueSuggestions(inputValue, partial.key);
    } else {
      this.loadKeySuggestions(inputValue);
    }
  };

  /**
   * On input change, we will start searching for suggestions
   * either locally or remotely using promise. Also it calls
   * onInputChange user handler if provided in props.
   *
   * @param {SyntheticEvent} event generated change event
   *
   * @return {undefined}
   */
  onInputChange = event => {
    const { partial } = this.state;
    const inputValue = event.target.value;

    this.setState({ inputValue });

    if (partial) {
      this.debouncedLoadValueSuggestions(inputValue, partial.key);
    } else {
      this.debouncedLoadKeySuggestions(inputValue);
    }
  };

  /**
   * On input keydown
   *
   * @param {SyntheticEvent} event SyntheticEvent event
   * @param {null|number} highlightedItemIndex last highlighted item
   * @param {null|number} newHighlightedItemIndex current highlighted item
   *
   * @return {undefined}
   */
  onKeyDown = (event, { highlightedItemIndex, newHighlightedItemIndex }) => {
    const { key } = event;

    switch (key) {
      case "ArrowDown":
      case "ArrowUp":
        this.onArrowUpOrDownPressed(event, newHighlightedItemIndex);
        break;
      case "Escape":
        this.onEscapePressed(event);
        break;
      case "Backspace":
      case "Delete":
        this.onBackspaceOrDeletePressed(event);
        break;
      case "Tab":
      case "Enter":
        this.onTabOrEnterPressed(event, highlightedItemIndex, key);
        break;
      default:
        break;
    }
  };

  /**
   * Checks how many subsequent items are disabled in either direction.
   *
   * @param {number} direction 1 or -1 for down and up respectively
   * @param {number} currentIndex current index of highlighted item
   *
   * @return {number} total number of subsequent items
   */
  getNextSubsequentDisabled = (direction, currentIndex) => {
    const { menuItems } = this.state;
    const min = 0;
    const max = menuItems.length - 1;
    let total = 0;
    let item;

    if (direction === 1) {
      for (let i = currentIndex; i <= max; i++) {
        item = this.getItemByIndex(i);
        if (item.isDisabled) {
          total += 1;
        } else {
          break;
        }
      }
    }

    if (direction === -1) {
      for (let i = currentIndex; i >= min; i--) {
        item = this.getItemByIndex(i);
        if (item.isDisabled) {
          total += 1;
        } else {
          break;
        }
      }
    }

    return total;
  };

  /**
   * On arrow up/down key pressed
   *
   * @param {SyntheticEvent} event SyntheticEvent for keydown
   * @param {number} itemIndex highlighted item index
   *
   * @returns {undefined}
   */
  onArrowUpOrDownPressed = (event, itemIndex) => {
    event.preventDefault();
    const { menuItems, inputValue: stateInputValue } = this.state;
    const direction = event.key === "ArrowUp" ? -1 : 1;

    if (itemIndex === null) {
      itemIndex = direction === 1 ? 0 : menuItems.length - 1;
    }

    const subsequentDisabled = this.getNextSubsequentDisabled(
      direction,
      itemIndex
    );
    const addIndex =
      event.key === "ArrowUp"
        ? -1 * subsequentDisabled
        : 1 * subsequentDisabled;
    const selectedItem = this.getItemByIndex(itemIndex);
    const newIndex =
      selectedItem && selectedItem.isDisabled
        ? itemIndex + addIndex
        : itemIndex;
    const nextItem =
      newIndex !== itemIndex ? this.getItemByIndex(newIndex) : selectedItem;
    const inputValue = nextItem
      ? this.getInputValueFromMenuItem(nextItem)
      : stateInputValue;

    this.setState({
      highlightedItemIndex: newIndex,
      inputValue
    });
  };

  /**
   * On escape key press. Hide the dropdown menu.
   *
   * @param {SyntheticEvent} event SyntheticEvent for keydown
   *
   * @returns {undefined}
   */
  onEscapePressed = event => {
    event.preventDefault();
    this.hideSuggestions();
  };

  /**
   * On backspace key press. If the input value is empty
   * and a partial tag exists, remove this tag otherwise
   * remove the last selected tag in input field.
   *
   * @param {SyntheticEvent} event SyntheticEvent for keydown
   *
   * @returns {undefined}
   */
  onBackspaceOrDeletePressed = event => {
    const { target } = event;

    if (target.value === "") {
      if (this.isPartialExist()) {
        this.deletePartial();
      } else {
        this.removeLastSelectedTag();
      }
    }
  };

  /**
   * On tab/enter key pressed. On both of these events, we
   * will select the highlighted item. If nothing is in the
   * list, then we will create a partial tag out of input value.
   *
   * @param {SyntheticEvent} event SyntheticEvent for keydown
   * @param {number} itemIndex highlighted item index
   *
   * @returns {undefined}
   */
  onTabOrEnterPressed = (event, itemIndex, key) => {
    const { inputValue } = this.state;

    if (key === "Tab" && inputValue === "") {
      this.triggerBlur();
      return;
    }

    event.preventDefault();

    const item = this.getItemByIndex(itemIndex);
    this.selectMenuItem(item);
  };

  /**
   * Utility method to get the item by index from dropdown
   * menu items list.
   *
   * @param {number} itemIndex highlighted item index
   *
   * @return {null|object}
   */
  getItemByIndex = itemIndex => {
    const { menuItems } = this.state;
    return itemIndex >= 0 ? menuItems[itemIndex] : null;
  };

  /**
   * Get input field value from the item object. By default,
   * we check for label property on the object but we can give
   * a callback to user to provide any custom value.
   *
   * @param {*} item item object
   *
   * @return {string} text value to form tag
   */
  getInputValueFromMenuItem = item => {
    const { renderTagKey, renderTagValue } = this.props;
    const getFn = this.isPartialExist() ? renderTagValue : renderTagKey;
    return getFn(item);
  };

  /**
   * Actually selects an item from the dropdown menus. It gets
   * the text value from a selected item and creates partial tag
   * if it doesn't exist. If a partial tag already exists, it
   * completes that tag. Also we need to set focus back in the input.
   *
   * @param {object} item item object
   *
   * @return {undefined}
   */
  selectMenuItem = item => {
    const { allowCustomKey, allowCustomValue, enableCustomTagWithoutKey } =
      this.props;
    const { inputValue } = this.state;
    const inputValueFromItem = item
      ? this.getInputValueFromMenuItem(item)
      : inputValue;

    if (!item && !inputValue.trim()) {
      return;
    }

    if (!item && inputValue && enableCustomTagWithoutKey) {
      this.createCustomTagWithoutKey(inputValueFromItem);
      return;
    }

    if (
      (this.isPartialExist() && !allowCustomValue && !item) ||
      (!this.isPartialExist() && !allowCustomKey && !item)
    ) {
      return;
    }

    if (inputValueFromItem) {
      if (this.isPartialExist()) {
        this.completePartial(inputValueFromItem, item);
      } else {
        this.createPartial(inputValueFromItem, item);
      }
    }

    // Needed to add a small timeout as the input quickly gets blured on select
    setTimeout(() => {
      if (this.inputRef) {
        this.inputRef.focus();
      }
    }, 200);
  };

  /**
   * Check if a partial tag already exists
   *
   * @returns {boolean}
   */
  isPartialExist = () => {
    const { partial } = this.state;
    return partial !== null;
  };

  /**
   * Check if the user has selected same key value pair. If
   * yes then this partial is not eligible to be created a new tag.
   *
   * @param {object} nextTag tag object
   * @returns {boolean}
   */
  isPartialEligibleForTag = nextTag => {
    const { value: selectedTags } = this.props;
    const foundTags = selectedTags.filter(
      ({ key, value }) =>
        key &&
        key.label === nextTag.key.label &&
        isObject(value) &&
        value.label === nextTag.value.label
    );

    return foundTags.length === 0;
  };

  /**
   * Check if the user has selected same key value pair. If
   * yes then this partial is not eligible to be created a new tag.
   *
   * @param {object} nextTag tag object
   * @returns {boolean}
   */
  isEligibleForCustomTagWithoutKey = nextTag => {
    const { value: selectedTags } = this.props;
    const foundTags = selectedTags.filter(
      ({ value }) => !isObject(value) && value === nextTag.value
    );

    return foundTags.length === 0;
  };

  /**
   * Load key suggestions based on input value
   *
   * @param {string} inputValue value of the text input
   *
   * @return {undefined}
   */
  loadKeySuggestions = inputValue => {
    const { loadKeyOptions, keyOptions } = this.props;

    if (isFunction(loadKeyOptions)) {
      const promise = loadKeyOptions(inputValue);

      if (!(promise instanceof Promise)) {
        throw new Error("loadKeyOptions must return a promise");
      }

      this.setState({
        isLoading: true,
        menuItems: [],
        highlightedItemIndex: null
      });

      promise
        .then(keys => {
          this.setState({
            menuItems: keys || [],
            isLoading: false,
            highlightedItemIndex: null
          });
        })
        .catch(() => {
          this.setState({
            menuItems: [],
            isLoading: false,
            highlightedItemIndex: null
          });
        });
    } else {
      this.setState({
        highlightedItemIndex: null,
        menuItems: this.filterSuggestions(inputValue, keyOptions)
      });
    }
  };

  /**
   * Load value suggestions based on input value
   *
   * @param {string} inputValue value of the text input
   *
   * @return {undefined}
   */
  loadValueSuggestions = (inputValue, partial) => {
    const { loadValueOptions } = this.props;
    const { valueOptions } = this.state;

    if (isFunction(loadValueOptions)) {
      const promise = loadValueOptions(inputValue, partial);

      if (!(promise instanceof Promise)) {
        throw new Error("loadValueOptions must return a promise");
      }

      this.setState({
        isLoading: true,
        menuItems: [],
        highlightedItemIndex: null
      });

      promise
        .then(values => {
          this.setState({
            menuItems: values || [],
            isLoading: false,
            highlightedItemIndex: null
          });
        })
        .catch(() => {
          this.setState({
            menuItems: [],
            isLoading: false,
            highlightedItemIndex: null
          });
        });
    } else {
      this.setState({
        menuItems: this.filterSuggestions(inputValue, valueOptions),
        highlightedItemIndex: null
      });
    }
  };

  /**
   * Filter an array of options based on input value
   *
   */
  filterSuggestions = (inputValue, options) => {
    this.setState({ highlightedItemIndex: null });
    return options.filter(
      o => o.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
    );
  };

  /**
   * Load and show values when a partial is created
   *
   * @param {object} partialKey partial key object
   *
   * @return {undefined}
   */
  loadValuesOnPartialCreate = partialKey => {
    const { onKeySelect } = this.props;

    if (isFunction(onKeySelect)) {
      const valuesPromise = onKeySelect(partialKey);

      if (!(valuesPromise instanceof Promise)) {
        throw new Error("onKeySelect must return a promise.");
      }

      if (valuesPromise instanceof Promise) {
        this.setState({ isLoading: true, highlightedItemIndex: null });

        valuesPromise
          .then(valueOptions => {
            this.setState({
              isLoading: false,
              valueOptions,
              menuItems: valueOptions,
              highlightedItemIndex: null
            });
          })
          .catch(() => {
            this.setState({
              isLoading: false,
              valueOptions: [],
              menuItems: [],
              highlightedItemIndex: null
            });
          });
      }
    } else {
      this.loadValueSuggestions("", partialKey);
    }
  };

  /**
   * Create a new partial tag from input value
   *
   * @param {string} inputValue text value of the input
   * @param {string} item found item with this text
   *
   * @returns {undefined}
   */
  createPartial = (inputValue, item) => {
    const halfTag = {
      label: inputValue,
      value: item ? item.value : inputValue,
      selectedItem: item
    };

    this.setState(
      {
        partial: { key: halfTag },
        inputValue: "",
        menuItems: [],
        highlightedItemIndex: null
      },
      () => {
        this.loadValuesOnPartialCreate(halfTag);
      }
    );
  };

  /**
   * Complete an existing partial tag
   *
   * @param {string} inputValue text value of input field
   * @param {string} item found item with this text
   *
   * @returns {undefined}
   */
  completePartial = (inputValue, item) => {
    const { partial } = this.state;
    const { onChange, onCreateTag, value: existingTags, name } = this.props;
    const nextTag = {
      key: {
        label: partial.key.label,
        value: partial.key.value,
        selectedItem: partial.key.selectedItem
      },
      value: {
        label: inputValue,
        value: item ? item.value : inputValue,
        selectedItem: item
      }
    };
    const shouldCreateTag = this.isPartialEligibleForTag(nextTag);

    if (shouldCreateTag) {
      const tags = [...existingTags, nextTag];

      if (isFunction(onChange)) {
        onChange(tags, name);
      }

      this.setFormikFieldValue(tags);

      if (isFunction(onCreateTag)) {
        onCreateTag(nextTag);
      }
    }

    this.setState(
      {
        partial: null,
        inputValue: "",
        menuItems: [],
        highlightedItemIndex: null,
        valueOptions: []
      },
      () => {
        this.loadKeySuggestions("");
      }
    );
  };

  /**
   * Create single plain tag
   *
   * @param {string} inputValue text value of input field
   *
   * @returns {undefined}
   */
  createCustomTagWithoutKey = inputValue => {
    const { onChange, onCreateTag, value: existingTags, name } = this.props;
    const nextTag = {
      value: inputValue
    };
    const shouldCreateTag = this.isEligibleForCustomTagWithoutKey(nextTag);

    if (shouldCreateTag) {
      const tags = [...existingTags, nextTag];

      if (isFunction(onChange)) {
        onChange(tags, name);
      }

      this.setFormikFieldValue(tags);

      if (isFunction(onCreateTag)) {
        onCreateTag(nextTag);
      }
    }

    this.setState(
      {
        partial: null,
        inputValue: "",
        menuItems: [],
        highlightedItemIndex: null,
        valueOptions: []
      },
      () => {
        this.loadKeySuggestions("");
      }
    );
  };

  /**
   * Delete an existing partial tag on backspace or delete press
   *
   * @returns {undefined}
   */
  deletePartial = () => {
    this.setState({ partial: null, inputValue: "" }, () => {
      this.loadKeySuggestions("");
    });
  };

  /**
   * Useful when the input value is empty and user still presses
   * backspace or delete key, we need to remove the last tag.
   * We also need to emit onChange with remaining tags.
   *
   * @return {undefined}
   */
  removeLastSelectedTag = () => {
    const { onChange, value: existingTags, name, onRemoveTag } = this.props;
    const cloned = [...existingTags];
    const lastIndex = cloned.length - 1;

    if (isFunction(onRemoveTag)) {
      onRemoveTag(cloned[lastIndex]);
    }

    cloned.splice(lastIndex, 1);

    if (isFunction(onChange)) {
      onChange(cloned, name);
    }

    this.setFormikFieldValue(cloned);
  };

  /**
   * Hide suggestions menu
   *
   * @return {undefined}
   */
  hideSuggestions = () => {
    this.setState({ menuItems: [], highlightedItemIndex: null });
  };

  /**
   * Render tags in the control input generated from value.
   *
   * @return {jsx} <Tag />
   */
  renderTags = () => {
    const { value: tags, isDisabled } = this.props;

    return (tags || []).map(({ key, value }, index) => (
      <Tag
        label={key ? key.label : null}
        value={isObject(value) && value.label ? value.label : value}
        key={`tagsel-tag-${index}`}
        onRemove={this.onRemoveTag(index)}
        isDisabled={isDisabled}
      />
    ));
  };

  /**
   * Render partial tag when only key has been selected.
   *
   * @return {null|jsx} <PartialTag />
   */
  renderPartial = () => {
    const { partial } = this.state;

    if (partial) {
      return <PartialTag label={partial.key.label} />;
    }

    return null;
  };

  /**
   * Get input placeholder based on key or value selection.
   *
   * @return {string} placeholder
   */
  getInputPlaceholder = () => {
    const { isLoading } = this.state;
    const {
      loadingKeysPlaceholder,
      loadingValuesPlaceholder,
      keyPlaceholder,
      valuePlaceholder
    } = this.props;

    if (this.isPartialExist()) {
      return isLoading ? loadingValuesPlaceholder : valuePlaceholder;
    }

    return isLoading ? loadingKeysPlaceholder : keyPlaceholder;
  };

  /**
   * Render input component
   *
   * @param {object} inputProps
   *
   * @return {jsx} <AutosizeInput />
   */
  renderInput = ({ className, onKeyDown }) => {
    const { inputValue } = this.state;
    const { isDisabled, ariaLabel } = this.props;
    const placeholder = this.getInputPlaceholder();

    return (
      <AutosizeInput
        inputClassName={className}
        value={inputValue}
        onChange={this.onInputChange}
        onFocus={this.onInputFocus}
        onKeyDown={onKeyDown}
        inputRef={element => {
          this.inputRef = element;
        }}
        disabled={isDisabled}
        autoComplete="off"
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    );
  };

  /**
   * Render main control component.
   *
   * @param {object} inputProps
   *
   * @return {jsx}
   */
  renderControl = inputProps => {
    const { isDisabled, isClearable, isInvalid, value, hideDownArrow } =
      this.props;
    const { focused, isLoading } = this.state;
    const showClearIcon = !isEmpty(value);

    return (
      <div
        className={gatherClasses(
          null,
          "oui-tagsel-control-container",
          focused && "oui-tagsel-control-container--focused",
          isDisabled && "oui-tagsel-control-container--disabled",
          isInvalid ? "oui-is-invalid" : null
        )}
        ref={this.controlRef}
        onClick={this.onControlContainerClick}
        aria-hidden="true"
      >
        <div className="oui-tagsel-value-container">
          {this.renderTags()}
          {this.renderPartial()}
          {this.renderInput(inputProps)}
        </div>
        <Indicators
          isLoading={isLoading}
          showClearIcon={showClearIcon}
          onClear={this.onClear}
          isClearable={isClearable}
          isDisabled={isDisabled}
          hideDownArrow={hideDownArrow}
        />
      </div>
    );
  };

  /**
   * Render a single menu item.
   *
   * @param {object} item single item object
   * @param {object}
   *
   * @return {jsx}
   */
  renderMenuItem = (item, { value }) => {
    const { renderMenuItemKey, renderMenuItemValue } = this.props;
    const getFn = this.isPartialExist()
      ? renderMenuItemValue
      : renderMenuItemKey;
    const children = getFn(item, value);

    return <MenuItem item={item}>{children}</MenuItem>;
  };

  /**
   * Render menu items container
   *
   * @param {object}
   *
   * @return {ReactPortal}
   */
  renderMenuItemsContainer = ({ children, containerProps }) => {
    const { maxMenuHeight } = this.props;
    const { ref, ...restContainerProps } = containerProps;
    const callRef = isolatedScroll => {
      if (isolatedScroll !== null) {
        ref(isolatedScroll.component);
        this.menuRef.current = isolatedScroll.component;
      }
    };

    const containerStyle = {
      overflowY: "auto",
      maxHeight: `${maxMenuHeight}px`
    };

    return ReactDOM.createPortal(
      <Popper
        modifiers={{
          preventOverflow: {
            escapeWithReference: true
          },
          applyStyles: {
            enabled: true,
            fn: data => /* istanbul ignore next  */ {
              data.styles.width = `${data.offsets.reference.width}px`;
              return data;
            }
          }
        }}
      >
        {({ ref: scrollRef, style }) => (
          <div
            ref={scrollRef}
            style={style}
            className="oui-tagsel-items-portal-wrapper"
          >
            <IsolatedScroll
              ref={callRef}
              {...restContainerProps}
              style={containerStyle}
            >
              {children}
            </IsolatedScroll>
          </div>
        )}
      </Popper>,
      document.querySelector("body")
    );
  };

  /**
   * On menu item mous enter to make it highlighted.
   *
   * @param {SynthenticEvent} event generated event
   * @param {object} object containing itemIndex
   *
   * @returns {undefined}
   */
  onItemMouseEnter = (_, { itemIndex }) => {
    const item = this.getItemByIndex(itemIndex);

    if (!item.isDisabled) {
      this.setState({
        highlightedItemIndex: itemIndex
      });
    }
  };

  /**
   * On menu item mous leave to make it unhighlighted.
   *
   * @returns {undefined}
   */
  onItemMouseLeave = () => {
    this.setState({
      highlightedItemIndex: null
    });
  };

  /**
   * On menu item mous down to make it selected.
   *
   * @param {number} itemIndex item index
   *
   * @returns {function} it returns a closure holding itemIndex
   */
  onItemMouseDown = itemIndex => event => {
    event.preventDefault();
    const item = this.getItemByIndex(itemIndex);

    if (!item.isDisabled) {
      this.selectMenuItem(item);
    }
  };

  /**
   * Get item props
   *
   * @param {object} object containing itemIndex
   *
   * @returns {object}
   */
  getItemProps = ({ itemIndex }) => ({
    onMouseEnter: this.onItemMouseEnter,
    onMouseLeave: this.onItemMouseLeave,
    onMouseDown: this.onItemMouseDown(itemIndex)
  });

  /**
   * Main render method of the component
   *
   * @returns {jsx}
   */
  render() {
    const { menuItems, highlightedItemIndex, popperKey } = this.state;
    const {
      disableInputFeedback,
      isInvalid,
      validationProps,
      name,
      dataTestId
    } = this.props;

    return (
      <div
        data-testid={dataTestId}
        className={gatherClasses(this.props, "oui-key-value-select-tagsel")}
      >
        <Manager key={popperKey}>
          <Reference>
            {({ ref }) => (
              <div ref={ref} className="oui-tagsel-popper-refrerence">
                <AutoWhatever
                  id={this.autowhateverId}
                  renderInputComponent={this.renderControl}
                  renderItem={this.renderMenuItem}
                  renderItemsContainer={this.renderMenuItemsContainer}
                  items={menuItems}
                  itemProps={this.getItemProps}
                  highlightedItemIndex={highlightedItemIndex}
                  theme={theme}
                  inputProps={{
                    onKeyDown: this.onKeyDown
                  }}
                />
              </div>
            )}
          </Reference>
        </Manager>
        {!disableInputFeedback && validationProps && (
          <InputFeedback
            show={isInvalid}
            message={get(validationProps.errors, name)}
          />
        )}
      </div>
    );
  }
}

TagSelect.propTypes = {
  allowCustomKey: PropTypes.bool,
  allowCustomValue: PropTypes.bool,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  debounceDelay: PropTypes.number,
  disableInputFeedback: PropTypes.bool,
  hideDownArrow: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  keyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      isDisabled: PropTypes.bool,
      tooltip: PropTypes.shape({
        placement: PropTypes.string,
        maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        overflowOnly: PropTypes.bool,
        showTooltip: PropTypes.bool
      })
    })
  ),
  keyPlaceholder: PropTypes.string,
  loadingKeysPlaceholder: PropTypes.string,
  loadKeyOptions: PropTypes.func,
  loadValueOptions: PropTypes.func,
  maxMenuHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeySelect: PropTypes.func,
  onRemoveTag: PropTypes.func,
  onCreateTag: PropTypes.func,
  renderMenuItemKey: PropTypes.func,
  renderMenuItemValue: PropTypes.func,
  renderTagKey: PropTypes.func,
  renderTagValue: PropTypes.func,
  validationProps: PropTypes.object,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      }),
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      ]).isRequired
    })
  ),
  valuePlaceholder: PropTypes.string,
  loadingValuesPlaceholder: PropTypes.string,
  enableCustomTagWithoutKey: PropTypes.bool,
  dataTestId: PropTypes.string,
  ariaLabel: PropTypes.string
};

TagSelect.defaultProps = {
  allowCustomKey: true,
  allowCustomValue: true,
  autoFocus: false,
  className: null,
  debounceDelay: 0,
  disableInputFeedback: false,
  hideDownArrow: false,
  isClearable: true,
  isDisabled: false,
  isInvalid: false,
  keyOptions: [],
  keyPlaceholder: "Select Key",
  loadingKeysPlaceholder: "Loading Keys...",
  loadKeyOptions: null,
  loadValueOptions: null,
  maxMenuHeight: 200,
  name: null,
  onBlur: null,
  onChange: null,
  onFocus: null,
  onKeySelect: null,
  onRemoveTag: null,
  onCreateTag: null,
  renderMenuItemKey: item => item.label,
  renderMenuItemValue: item => item.label,
  renderTagKey: item => item.label,
  renderTagValue: item => item.label,
  validationProps: null,
  value: [],
  valuePlaceholder: "Select Value",
  loadingValuesPlaceholder: "Loading Values...",
  enableCustomTagWithoutKey: false,
  dataTestId: "orion-tagselect"
};

TagSelect.displayName = "OrionTagSelect";

export default TagSelect;
